import type { TimelineData } from '@revelio/replots';

type SubsidiaryCountPoint = {
  date: string;
  total: number;
  concentration: number | null;
};

type SubsidiaryFlowYear = {
  date: string;
  joined: number;
  left: number;
  joinedIds: string[];
  leftIds: string[];
};

export type SubsidiaryTrends = {
  counts: SubsidiaryCountPoint[];
  flows: SubsidiaryFlowYear[];
};

type BuildSubsidiaryTrendsInput = {
  data: TimelineData;
  start?: string;
  now: number;
};

type Membership = {
  id: string;
  unitId: string;
  headcount: number;
  entry: number;
  exit: number;
};

type Span = { entry: number; exit: number; unitId: string };

const parseDate = (value?: string): number | null => {
  const trimmed = value?.trim() ?? '';
  if (/^\d{4}$/.test(trimmed)) return Date.UTC(Number(trimmed), 0, 1);
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const time = Date.parse(`${trimmed.slice(0, 10)}T00:00:00Z`);
    return Number.isNaN(time) ? null : time;
  }
  return null;
};

const isoDay = (time: number) => new Date(time).toISOString().slice(0, 10);

const memberships = (data: TimelineData): Membership[] => {
  const byId = new Map(data.entities.map((entity) => [entity.id, entity]));
  const rootId = data.entities.find(
    (entity) => entity.parentId === undefined || !byId.has(entity.parentId)
  )?.id;
  const resolved = new Map<string, Span>();
  const resolve = (id: string, visiting: Set<string>): Span => {
    const known = resolved.get(id);
    if (known !== undefined) return known;
    const entity = byId.get(id);
    const parentId = entity?.parentId;
    const parent =
      id === rootId ||
      parentId === undefined ||
      !byId.has(parentId) ||
      visiting.has(parentId)
        ? {
            entry: Number.NEGATIVE_INFINITY,
            exit: Number.POSITIVE_INFINITY,
            unitId: id,
          }
        : resolve(parentId, visiting.add(id));
    const join = id === rootId ? null : parseDate(entity?.joinedAt);
    const end = id === rootId ? null : parseDate(entity?.endedAt);
    const span = {
      entry: Math.max(parent.entry, join ?? parent.entry),
      exit: Math.min(parent.exit, end ?? Number.POSITIVE_INFINITY),
      unitId: parentId === rootId ? id : parent.unitId,
    };
    resolved.set(id, span);
    return span;
  };

  return data.entities.flatMap((entity) => {
    if (entity.id === rootId) return [];
    const { entry, exit, unitId } = resolve(entity.id, new Set());
    if (exit <= entry) return [];
    return [
      {
        id: entity.id,
        unitId,
        headcount: Math.max(entity.value ?? 0, 0),
        entry,
        exit,
      },
    ];
  });
};

const inGroupAt = (member: Membership, time: number) =>
  member.entry <= time && time < member.exit;

const concentrationAt = (
  members: Membership[],
  time: number
): number | null => {
  const units = new Map<string, number>();
  for (const member of members) {
    if (member.headcount > 0 && inGroupAt(member, time)) {
      units.set(
        member.unitId,
        (units.get(member.unitId) ?? 0) + member.headcount
      );
    }
  }
  const total = [...units.values()].reduce((sum, value) => sum + value, 0);
  if (total <= 0) return null;
  return [...units.values()].reduce(
    (sum, value) => sum + (value / total) ** 2,
    0
  );
};

const monthStarts = (from: number, to: number): number[] => {
  const first = new Date(from);
  const times: number[] = [];
  for (
    let cursor = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1);
    cursor <= to;
    cursor = Date.UTC(
      new Date(cursor).getUTCFullYear(),
      new Date(cursor).getUTCMonth() + 1,
      1
    )
  ) {
    times.push(cursor);
  }
  if (times[times.length - 1] !== to) times.push(to);
  return times;
};

export const buildSubsidiaryTrends = ({
  data,
  start,
  now,
}: BuildSubsidiaryTrendsInput): SubsidiaryTrends => {
  const members = memberships(data);
  const finiteEntries = members
    .map((member) => member.entry)
    .filter(Number.isFinite);
  const domainStart =
    parseDate(start) ??
    parseDate(data.startDate) ??
    (finiteEntries.length > 0 ? Math.min(...finiteEntries) : null);
  if (domainStart === null || domainStart >= now || members.length === 0) {
    return { counts: [], flows: [] };
  }

  const times = monthStarts(domainStart, now);
  const counts = times.map((time) => ({
    date: isoDay(time),
    total: members.filter((member) => inGroupAt(member, time)).length,
    concentration: concentrationAt(members, time),
  }));

  const firstYear = new Date(domainStart).getUTCFullYear();
  const lastYear = new Date(now).getUTCFullYear();
  const flows: SubsidiaryFlowYear[] = [];
  for (let year = firstYear; year <= lastYear; year += 1) {
    flows.push({
      date: `${year}-01-01`,
      joined: 0,
      left: 0,
      joinedIds: [],
      leftIds: [],
    });
  }
  const flowOf = (time: number) =>
    flows[new Date(time).getUTCFullYear() - firstYear];
  for (const member of members) {
    if (member.entry > domainStart && member.entry <= now) {
      const flow = flowOf(member.entry);
      if (flow !== undefined) {
        flow.joined += 1;
        flow.joinedIds.push(member.id);
      }
    }
    if (member.exit > domainStart && member.exit <= now) {
      const flow = flowOf(member.exit);
      if (flow !== undefined) {
        flow.left += 1;
        flow.leftIds.push(member.id);
      }
    }
  }

  return { counts, flows };
};

export const countSubsidiaryActivity = ({
  data,
  from,
  to,
}: {
  data: TimelineData;
  from: number;
  to: number;
}): { joined: number; left: number } => {
  const members = memberships(data);
  const within = (time: number) => time > from && time <= to;
  return {
    joined: members.filter((member) => within(member.entry)).length,
    left: members.filter((member) => within(member.exit)).length,
  };
};

export const countSubsidiariesAt = ({
  data,
  at,
}: {
  data: TimelineData;
  at: number;
}): number =>
  memberships(data).filter((member) => inGroupAt(member, at)).length;

export const subsidiaryConcentrationAt = ({
  data,
  at,
}: {
  data: TimelineData;
  at: number;
}): number | null => concentrationAt(memberships(data), at);
